import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import Stage from './components/Stage';
import Starfield from './components/Starfield';
import Display from './components/Display';
import NextPiece from './components/NextPiece';
import StartButton from './components/StartButton';
import { createStage, checkCollision } from './gameHelpers';
import TouchControls from './components/TouchControls';
import ControlsModal from './components/ControlsModal';
import { usePlayer } from './hooks/usePlayer';
import { useStage } from './hooks/useStage';
import { useGameStatus } from './hooks/useGameStatus';
import { useInterval } from './hooks/useInterval';
import { getHighScores, submitHighScore, HighScore, signInPromise, logEvent } from './firebase';
import HighScoreList from './components/HighScoreList';

const App: React.FC = () => {
  const [dropTime, setDropTime] = useState<number | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  // Line-clear animation trigger
  const [clearAnimation, setClearAnimation] = useState(false);
  // Block rapid drop actions (prevent multiple locks)
  const [actionBlocked, setActionBlocked] = useState(false);
  // Persistent best score
  const [bestScore, setBestScore] = useState<number>(0);
  // Audio settings
  const [musicVolume, setMusicVolume] = useState<number>(0.5);
  const [sfxVolume, setSfxVolume] = useState<number>(0.7);
  const [muted, setMuted] = useState<boolean>(false);
  // Track whether the game has started from the menu
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  // Pause state
  const [paused, setPaused] = useState<boolean>(false);
  // Shared high-score list state
  const [highScores, setHighScores] = useState<HighScore[]>([]);
  const [highScoresLoading, setHighScoresLoading] = useState<boolean>(true);
  const [menuLoaded, setMenuLoaded] = useState<boolean>(false);
  const [playerName, setPlayerName] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  
  // Enhanced visual effects
  const [pieceLocked, setPieceLocked] = useState<boolean>(false);
  const [newPieceEntry, setNewPieceEntry] = useState<boolean>(false);

  // Sound effects and music refs
  const rotateSound = useRef<HTMLAudioElement>(new Audio('/sounds/rotate.mp3'));
  const hardDropSound = useRef<HTMLAudioElement>(new Audio('/sounds/harddrop.mp3'));
  const clearSound = useRef<HTMLAudioElement>(new Audio('/sounds/clear.mp3'));
  const music = useRef<HTMLAudioElement>(new Audio('/sounds/music.mp3'));

  const [player, updatePlayerPos, resetPlayer, rotatePlayer, nextTetromino] = usePlayer();
  const [stage, setStage, rowsCleared, shouldCheckGameOver] = useStage(player, resetPlayer);
  const [score, setScore, rows, setRows, level, setLevel] = useGameStatus(rowsCleared);
  
  // Load and persist best score
  useEffect(() => {
    const stored = localStorage.getItem('bestScore');
    if (stored) {
      setBestScore(parseInt(stored, 10));
    }
  }, []);
  useEffect(() => {
    if (gameOver && score > bestScore) {
      setBestScore(score);
      localStorage.setItem('bestScore', score.toString());
    }
  }, [gameOver, score, bestScore]);
  // Pause background music on game over
  useEffect(() => {
    if (gameOver) {
      music.current.pause();
    }
  }, [gameOver]);
  // Set music to loop
  useEffect(() => {
    music.current.loop = true;
  }, []);

  // Update audio volumes whenever settings change
  useEffect(() => {
    music.current.volume = muted ? 0 : musicVolume;
    rotateSound.current.volume = muted ? 0 : sfxVolume;
    hardDropSound.current.volume = muted ? 0 : sfxVolume;
    clearSound.current.volume = muted ? 0 : sfxVolume;
  }, [musicVolume, sfxVolume, muted]);
  // Detect touch support to show touch controls only on mobile
  useEffect(() => {
    const touchSupported = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    setIsTouch(touchSupported);
  }, []);
  // Clear action block once a new piece is spawned (collided resets)
  useEffect(() => {
    if (actionBlocked && !player.collided) {
      setActionBlocked(false);
    }
  }, [player.collided, actionBlocked]);
  // Trigger clear animation, sound, & haptic on row clears
  useEffect(() => {
    if (rowsCleared > 0) {
      // Analytics: lines cleared
      logEvent('lines_cleared', { count: rowsCleared });
      // Play clear sound and vibrate
      clearSound.current.currentTime = 0;
      clearSound.current.play();
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      // Visual line-clear animation
      setClearAnimation(true);
      const timer = setTimeout(() => setClearAnimation(false), 300);
      return () => clearTimeout(timer);
    }
  }, [rowsCleared]);
  
  // Trigger menu animation after high scores are loaded
  useEffect(() => {
    setHighScoresLoading(true);
    signInPromise
      .then(() => getHighScores())
      .then((scores) => {
        setHighScores(scores);
        setHighScoresLoading(false);
        
        // Delay setting menuLoaded to allow for a smooth animation
        setTimeout(() => {
          setMenuLoaded(true);
        }, 200);
      })
      .catch(err => {
        console.error(err);
        setHighScoresLoading(false);
        setMenuLoaded(true); // Still show the menu even if there's an error
      });
  }, []);
  
  useEffect(() => {
    if (gameOver) {
      setHighScoresLoading(true);
      getHighScores()
        .then((scores) => {
          setHighScores(scores);
          setHighScoresLoading(false);
        })
        .catch(err => {
          console.error(err);
          setHighScoresLoading(false);
        });
    }
  }, [gameOver]);

  // Check for game over when flagged by useStage
  useEffect(() => {
    if (shouldCheckGameOver && !gameOver) {
      setGameOver(true);
      setDropTime(null);
    }
  }, [shouldCheckGameOver, gameOver]);

  // Track piece entry
  useEffect(() => {
    // When a new piece is created and not from collision/locking
    if (!player.collided && player.pos.y === 0) {
      setNewPieceEntry(true);
      setTimeout(() => setNewPieceEntry(false), 300);
    }
  }, [player.pos.y, player.collided]);

  const movePlayer = (dir: number) => {
    if (!checkCollision(player, stage, { x: dir, y: 0 })) {
      updatePlayerPos({ x: dir, y: 0, collided: false });
    }
  };

  const startGame = () => {
    // Enter game view
    setGameStarted(true);
    setPaused(false);
    // Initialize game state
    setStage(createStage());
    resetPlayer();
    setGameOver(false);
    setDropTime(1000);
    setScore(0);
    setRows(0);
    setLevel(0);
    // Start background music
    music.current.currentTime = 0;
    music.current.play().catch(() => {/* play prevented */});
    // Analytics: game start or restart
    logEvent('game_start');
  };
  // Handle submitting the player's score
  const handleSubmitScore = async () => {
    if (!playerName) return;
    
    setHighScoresLoading(true);
    try {
      await submitHighScore(playerName, score);
      setHasSubmitted(true);
      const updated = await getHighScores();
      setHighScores(updated);
    } catch (error) {
      console.error("Error submitting score:", error);
    } finally {
      setHighScoresLoading(false);
    }
  };
  // Pause and resume handlers
  const pauseGame = useCallback(() => {
    setPaused(true);
    setDropTime(null);
  }, []);
  const resumeGame = useCallback(() => {
    setPaused(false);
    // restore drop speed based on level
    setDropTime(1000 / (level + 1) + 200);
  }, [level]);

  const drop = () => {
    if (rows > (level + 1) * 10) {
      setLevel(level + 1);
      setDropTime(1000 / (level + 1) + 200);
    }

    if (!checkCollision(player, stage, { x: 0, y: 1 })) {
      updatePlayerPos({ x: 0, y: 1, collided: false });
    } else {
      // Check if the piece has settled at the top (game over condition)
      if (player.pos.y === 0) {
        setGameOver(true);
        setDropTime(null);
        return;
      }
      
      // Lock the current piece in place
      updatePlayerPos({ x: 0, y: 0, collided: true });
      
      // Trigger visual piece lock effect
      setPieceLocked(true);
      setTimeout(() => setPieceLocked(false), 250);
    }
  };

  useInterval(drop, dropTime);

  // Touch control handlers
  const softDrop = () => {
    if (paused || gameOver || actionBlocked || player.collided) return;
    drop();
  };
  const hardDrop = () => {
    if (paused || gameOver || actionBlocked || player.collided) return;
    // Block further drop until reset
    setActionBlocked(true);
    // Play hard-drop sound and haptic feedback
    hardDropSound.current.currentTime = 0;
    hardDropSound.current.play();
    if (navigator.vibrate) navigator.vibrate(50);
    
    let offset = 0;
    // Compute how far the piece can drop
    while (!checkCollision(player, stage, { x: 0, y: offset + 1 })) {
      offset += 1;
    }
    
    if (offset > 0) {
      // Check if the final position would be at the top (game over)
      if (player.pos.y + offset === 0) {
        setGameOver(true);
        setDropTime(null);
        return;
      }
      
      // Lock the current piece in place at the landing position
      updatePlayerPos({ x: 0, y: offset, collided: true });
      
      // Trigger lock effect at the landing position
      setPieceLocked(true);
      setTimeout(() => setPieceLocked(false), 250);
    }
  };

  // Compute ghost piece preview positions
  const ghostCells = React.useMemo(() => {
    // Only show ghost when game is active and piece exists
    if (gameOver || dropTime === null) return [];
    // Determine how far the piece can fall by collision detection
    let offset = 0;
    // Move down until a collision would occur
    while (!checkCollision(player, stage, { x: 0, y: offset + 1 })) {
      offset += 1;
    }
    const ghostY = player.pos.y + offset;
    // Build list of cell positions for the ghost piece
    const cells: { x: number; y: number }[] = [];
    player.tetromino.forEach((row, y) => {
      row.forEach((value, x) => {
        if (value !== 0) {
          cells.push({ x: player.pos.x + x, y: ghostY + y });
        }
      });
    });
    return cells;
  }, [player, stage, gameOver, dropTime]);

  // Disable exhaustive-deps warning for external handler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const keyUp = ({ keyCode }: KeyboardEvent) => {
    if (!gameOver) {
      if (keyCode === 40) {
        setDropTime(1000 / (level + 1) + 200);
      }
    }
  };

  // Disable exhaustive-deps warning for external handler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const move = (e: KeyboardEvent) => {
    const { keyCode, repeat } = e;
    if (!gameOver) {
      // Spacebar triggers hard drop
      if (keyCode === 32 && !repeat) {
        e.preventDefault();
        hardDrop();
        return;
      }
      if (keyCode === 37) {
        movePlayer(-1);
      } else if (keyCode === 39) {
        movePlayer(1);
      } else if (keyCode === 40) {
        drop();
      } else if (keyCode === 38) {
        // Rotate piece and play SFX
        rotatePlayer(stage, 1);
        rotateSound.current.currentTime = 0;
        rotateSound.current.play();
      }
    }
  };

  // Disable exhaustive-deps warning for move and keyUp defined outside
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener('keydown', move);
    window.addEventListener('keyup', keyUp);
    return () => {
      window.removeEventListener('keydown', move);
      window.removeEventListener('keyup', keyUp);
    };
  }, [move, keyUp, gameOver]);
  // Pause/unpause via keyboard (Esc or P)
  useEffect(() => {
    const handlePauseKey = (e: KeyboardEvent) => {
      if (!gameStarted || gameOver) return;
      if (e.key === 'Escape' || e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        if (paused) resumeGame();
        else pauseGame();
      }
    };
    window.addEventListener('keydown', handlePauseKey);
    return () => window.removeEventListener('keydown', handlePauseKey);
  }, [gameStarted, gameOver, paused, pauseGame, resumeGame]);

  // Show main menu until game is started
  if (!gameStarted) {
    return (
      <>
        <div className="menu-wrapper">
          <Starfield />
          <h1 className="menu-title">Plattfall</h1>
          
          <div className={`loading-overlay ${!highScoresLoading ? 'hidden' : ''}`}>
            <div className="main-loading-spinner"></div>
            <div className="loading-text">Loading Game...</div>
          </div>
          
          <div className={`menu-container ${menuLoaded ? 'loaded' : ''}`}>
            <div className="button-group">
              <StartButton callback={startGame} />
              <button
                type="button"
                onClick={() => setShowControls(true)}
                className="start-button"
              >
                Controls
              </button>
            </div>
            
            <HighScoreList highScores={highScores} isLoading={false} />
          </div>
        </div>
        {showControls && <ControlsModal onClose={() => setShowControls(false)} />}
      </>
    );
  }
  // Show game-over modal overlay when the game ends
  if (gameOver) {
    return (
      <>
        <div className="menu-wrapper">
          <Starfield />
          <h1 className="menu-title">Game Over</h1>
          
          <div className={`menu-container ${!highScoresLoading ? 'loaded' : ''}`}>
            <Display text={`Score: ${score}`} />
            <Display text={`Best: ${bestScore}`} />
            
            {/* Shared leaderboard */}
            <HighScoreList highScores={highScores} isLoading={highScoresLoading} />
            
            {/* Name submission form */}
            {!hasSubmitted && !highScoresLoading && (
              <div className="submit-score">
                <input
                  type="text"
                  placeholder="Your name"
                  value={playerName}
                  onChange={e => setPlayerName(e.target.value)}
                />
                <button
                  type="button"
                  className="start-button"
                  onClick={handleSubmitScore}
                >
                  Submit Score
                </button>
              </div>
            )}
            
            <StartButton callback={startGame} />
          </div>
        </div>
      </>
    );
  }
  return (
    <div className="tetris-wrapper">
      <Starfield />
      {/* Mobile mini-bar: next piece, score, and pause */}
      {isTouch && !paused && (
        <div className="mini-bar">
          {!gameOver ? (
            <>
              <div className="mini-bar-next">
                <NextPiece shape={nextTetromino.shape} size={50} />
              </div>
              <div className="mini-bar-score">{score}</div>
              <button
                type="button"
                className="mini-bar-pause-btn"
                onClick={pauseGame}
              >⏸</button>
            </>
          ) : (
            <>
              <div className="mini-bar-score">Score: {score}</div>
              <div className="mini-bar-score">Best: {bestScore}</div>
              <div className="mini-bar-score">Rows: {rows}</div>
              <div className="mini-bar-score">Level: {level}</div>
            </>
          )}
        </div>
      )}
      <div className={`tetris${clearAnimation ? ' line-clear' : ''}`}>  
          <Stage
            stage={stage}
            ghostCells={ghostCells}
            onDoubleTap={hardDrop}
            isMobile={isTouch}
            pieceLocked={pieceLocked}
            newPieceEntry={newPieceEntry}
          />
          {(!isTouch || gameOver) && (
            <aside>
            {gameOver ? (
              <div className="aside-content">
                <Display text="Game Over" gameOver />
                <Display text={`Score: ${score}`} />
                <Display text={`Best: ${bestScore}`} />
                {!hasSubmitted && !highScoresLoading && (
                  <div className="submit-score">
                    <input
                      type="text"
                      placeholder="Your name"
                      value={playerName}
                      onChange={e => setPlayerName(e.target.value)}
                    />
                    <button 
                      className="start-button"
                      onClick={handleSubmitScore}
                    >
                      Submit Score
                    </button>
                  </div>
                )}
                <HighScoreList highScores={highScores} isLoading={highScoresLoading} />
                <StartButton callback={startGame} />
              </div>
            ) : (
              <div className="aside-content">
                <Display text="Next" />
                <NextPiece shape={nextTetromino.shape} />
                <Display text={`Score: ${score}`} />
                <Display text={`Best: ${bestScore}`} />
                <Display text={`Rows: ${rows}`} />
                <Display text={`Level: ${level}`} />
                <button
                  type="button"
                  onClick={pauseGame}
                  className="start-button"
                >
                  Pause
                </button>
              </div>
            )}
            </aside>
          )}
      </div>
      {isTouch && !paused && (
        <TouchControls
          moveLeft={() => movePlayer(-1)}
          moveRight={() => movePlayer(1)}
          rotate={() => rotatePlayer(stage, 1)}
          softDrop={softDrop}
          hardDrop={hardDrop}
        />
      )}
      {showControls && (
        <ControlsModal onClose={() => setShowControls(false)} />
      )}
      {/* Pause menu overlay */}
      {paused && !showControls && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Paused</h2>
            <button
              type="button"
              onClick={resumeGame}
              className="start-button"
            >
              Resume
            </button>
            <br/>
            <button
              type="button"
              onClick={startGame}
              className="start-button"
            >
              Restart
            </button>
            <button
              type="button"
              onClick={() => setShowControls(true)}
              className="start-button"
            >
              Controls
            </button>
            <div className="sound-controls">
              <label>
                Music Volume
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={musicVolume}
                  onChange={e => setMusicVolume(parseFloat(e.target.value))}
                />
              </label>
              <label>
                SFX Volume
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={sfxVolume}
                  onChange={e => setSfxVolume(parseFloat(e.target.value))}
                />
              </label>
              <button
                type="button"
                className="start-button"
                onClick={() => setMuted(!muted)}
              >
                {muted ? 'Unmute' : 'Mute'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Game Over menu overlay on mobile */}
      {isTouch && gameOver && !paused && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Game Over</h2>
            <Display text={`Score: ${score}`} />
            <Display text={`Best: ${bestScore}`} />
            {!hasSubmitted && !highScoresLoading && (
              <div className="submit-score">
                <input
                  type="text"
                  placeholder="Your name"
                  value={playerName}
                  onChange={e => setPlayerName(e.target.value)}
                />
                <button 
                  className="start-button"
                  onClick={handleSubmitScore}
                >
                  Submit Score
                </button>
              </div>
            )}
            <HighScoreList highScores={highScores} isLoading={highScoresLoading} />
            <StartButton callback={startGame} />
          </div>
        </div>
      )}
    </div>
  );
};

export default App;